import { Injectable } from '@angular/core';
import { Coordinate } from 'ol/coordinate';

import * as Extent from 'ol/extent';
import * as olProj from 'ol/proj';


import { TTCoordinate } from '../models/tt-coordinate';

@Injectable({
  providedIn: 'root'
})
export class CoordinatesService {

  constructor() { }

  /**
   * Processes the coordinates according to their type and returns an object of type TTCoordinate
   * @param value coordinates as string
   * @param mapProjectionCode map projection code as string
   * @returns TTCoordinate object
   */
  handleCoords(value: string, mapProjectionCode: string): TTCoordinate {

    let ttCoord: TTCoordinate = null;
    let coordinateExtractor: CoordinateExtractor = new CoordinateExtractor(mapProjectionCode);

    ttCoord = coordinateExtractor.getCoordinates(value);
    return ttCoord != null ? ttCoord : null;
  }
}

/* ===================================== */
/* Inner classes
/* ===================================== */

abstract class Coord {
  value: string;
  mapProjectionCode: string;

  constructor(value: string, mapProjectionCode: string) {
    this.value = value;
    this.mapProjectionCode = mapProjectionCode;
  }

  abstract getCoordinates(): TTCoordinate;
}

class XYCoord extends Coord {
  // readonly Lb08Extent = [522000, 520920, 798420, 744300];
  readonly Lb08Extent = [510000, 510000, 800000, 750000];
  readonly Lb72Extent = [17736.0314, 23697.0977, 297289.9391, 245375.4223];
  readonly WGS84Extent = [-7, 45.5100, 9.4000, 63.5000];

  constructor(value: string, mapProjectionCode: string) {
    super(value, mapProjectionCode);
  }

  /* ===================================== */
  /* PUBLIC
  /* ===================================== */

  /**
   * Builds the TTCoordinate object
   * @returns TTCoordinate
   */
  getCoordinates(): TTCoordinate {
    let coordArray: number[] = this._getCoordArray();
    let coordSystem: string = this._getCoordSystem(coordArray);
    let reprojectedCoordArray: number[] = this._reprojectCoordinates(coordArray, coordSystem);
    let coordAsString: string = this._getCoordAsString(coordArray, coordSystem);
    return new TTCoordinate(reprojectedCoordArray, coordSystem, coordAsString)
  }

  /* ===================================== */
  /* PRIVATE
  /* ===================================== */

  /**
   * Transforms coordinates passed in string into an array.
   * @returns coordinates as number array
   */
  _getCoordArray(): number[] {
    let coordsArray = null;
    this.value = this.value.replace(/′/g, "'").replace(/″/g, '"');

    //lat lon EPSG:4326
    //xx.xxx°, xx.xxx°
    //xx.xxx°, N xx.xxx° E
    this.value = this.value.toUpperCase().replace(/°/g, ' ').replace(/[A-Z]/g, ' ').replace(/\s{2,}/g, ' ').replace(/^\s+|\s+$/g, '');
    //var xy = val.split(' ');
    var xy = this.value.split(',');
    if (xy.length === 2) {
      //if necessary we will switch x and y afterwards, for now we presume lat lon, not lon lat
      var y = parseFloat(xy[0]);
      var x = parseFloat(xy[1]);
      coordsArray = [y, x];
    }
    return coordsArray;
  }

  /**
   * Returns a textual description of the coordinates.
   * @param coordArray coordinates as number array
   * @param coordSystem coordinates system as string
   * @returns string description of coordinates
   */
  private _getCoordAsString(coordArray: number[], coordSystem: string): string {
    let locationString: string = null;
    if (coordSystem === "Lb08") {
      locationString = "x: " + coordArray[0].toFixed(2) + " m <br />y: " + coordArray[1].toFixed(2) + " m";
    } else if (coordSystem === "Lb72") {
      locationString = "x: " + coordArray[0].toFixed(2) + " m <br />y: " + coordArray[1].toFixed(2) + " m";
    } else if (coordSystem === "WGS84") {
      locationString = "φ: " + coordArray[1].toFixed(6) + "°<br /> λ: " + coordArray[0].toFixed(6) + "°";
    }
    return locationString;
  }

  /**
   * Reprojects the coordinates in the desired projection system
   * @param coordArray coordinates as number array
   * @param coordSystem coordinates system as string
   * @returns reprojected coordinates as number array
   */
  private _reprojectCoordinates(coordArray: number[], coordSystem: string) {
    let coords = null;
    if (coordSystem === "Lb08") {
      coords = olProj.transform(coordArray, 'EPSG:3812', this.mapProjectionCode);
    } else if (coordSystem === "Lb72") {
      coords = olProj.transform(coordArray, 'EPSG:31370', this.mapProjectionCode);
    } else if (coordSystem === "WGS84") {
      //reverse if needed
      if (Extent.containsCoordinate(this.WGS84Extent, coordArray) || Extent.containsCoordinate(this.WGS84Extent, coordArray.reverse())) {
        coords = olProj.transform(coordArray, 'EPSG:4326', this.mapProjectionCode);
      }
    }
    return coords;
  }

  /**
   * Returns the projection system as string
   * @param coordArray coordinates as number array
   * @returns projection system as string
   */
  private _getCoordSystem(coordArray: number[]): string {
    let coordSystem = null;
    if (Extent.containsCoordinate(this.Lb08Extent, coordArray)) {
      coordSystem = "Lb08";
    } else if (Extent.containsCoordinate(this.Lb72Extent, coordArray)) {
      coordSystem = "Lb72";
    } else if (Extent.containsCoordinate(this.WGS84Extent, coordArray) || Extent.containsCoordinate(this.WGS84Extent, coordArray.reverse())) {
      coordSystem = "WGS84";
    }
    return coordSystem;
  }
}

class UTMCoord extends Coord {
  readonly Utm31Extent = [-7, 45.5100, 9.4000, 63.5000];
  readonly UtmYExtent = [5400000, 5750000];

  constructor(value: string, mapProjectionCode: string) {
    super(value, mapProjectionCode);
  }

  /* ===================================== */
  /* PUBLIC
  /* ===================================== */

  /**
  * Builds the TTCoordinate object
  * @returns TTCoordinate
  */
  getCoordinates(): TTCoordinate {
    let coordArray: number[] = this._getCoordArray();
    let coordSystem: string = this._getCoordSystem(coordArray);
    let coordAsString: string = this._getCoordAsString(coordArray);
    let reprojectedCoordArray: number[] = this._reprojectCoordinates(coordArray);
    return new TTCoordinate(reprojectedCoordArray, coordSystem, coordAsString);
  }

  /* ===================================== */
  /* PRIVATE
  /* ===================================== */

  /**
   * Transforms coordinates passed in string into an array.
   * @returns coordinates as number array
   */
  private _getCoordArray(): number[] {
    let coord: number[] = this.value.replace(/  +/g, ' ').slice(3, this.value.length).trim().split(/,/, 2).map(parseFloat);
    return coord;
  }

  /**
   * Returns the projection system
   * @param coordArray 
   * @returns coordonate system as string
   */
  private _getCoordSystem(coordArray: number[]): string {
    let zone: number = this._getZone();

    let coordSystem = null;
    let lonlatCoords = olProj.transform(coordArray, 'EPSG:326' + zone.toString(), 'EPSG:4258');

    if ((this._utmZoneFromLon(lonlatCoords[0]) === zone) && (coordArray[1] > this.UtmYExtent[0] && coordArray[1] < this.UtmYExtent[1])) {
      coordSystem = "UTM";
    }
    return coordSystem;
  }

  /**
   * Returns a textual description of the coordinates
   * @param coords array of coordinates
   * @returns 
   */
  private _getCoordAsString(coords: number[]): string {
    let zone: number = this._getZone();
    return "Zone: " + zone + "N <br />" + "x: " + coords[0].toFixed(2) + " m <br />y: " + coords[1].toFixed(2) + " m";
  }

  /**
   * Reproject an array of coordinates
   * @param coordinates 
   * @returns array of coordinates
   */
  private _reprojectCoordinates(coordinates: number[]) {
    return olProj.transform(coordinates, 'EPSG:326' + this._getZone(), this.mapProjectionCode);
  }

  /**
   * Returns the utm zone based on the longitude
   * @param lonDeg 
   * @returns utm zone as number
   */
  private _utmZoneFromLon(lonDeg: number): number {
    return Math.floor((((lonDeg + 180) / 6) % 60) + 1);
  }

  /**
   * Extracts the zone from a string
   * @returns zone as number
   */
  private _getZone(): number {
    return parseFloat(this.value.slice(0, 2));
  }
}

class DSMCoord extends Coord {
  readonly Lb08Extent = [522000, 520920, 798420, 744300];
  readonly Lb72Extent = [17736.0314, 23697.0977, 297289.9391, 245375.4223];
  readonly WGS84Extent = [-7, 45.5100, 9.4000, 63.5000];
  private fullDegreesLocationString: string[];

  constructor(value: string, mapProjectionCode: string) {
    super(value, mapProjectionCode);
  }

  /* ===================================== */
  /* PUBLIC
  /* ===================================== */

  /**
  * Builds the TTCoordinate object
  * @returns TTCoordinate
  */
  getCoordinates(): TTCoordinate {
    let coordArray: number[] = this._getCoordArray();
    let coordSystem: string = this._getCoordSystem(coordArray);
    let reprojectedCoordArray: number[] = this._reprojectCoordinates(coordArray, coordSystem);
    let coordAsString: string = this._getCoordAsString(coordArray, coordSystem);
    return new TTCoordinate(reprojectedCoordArray, coordSystem, coordAsString)
  }

  /* ===================================== */
  /* PRIVATE
  /* ===================================== */

  /**
   * Extracts an array of coordinates from a string
   * Todo: refactor this method (too obscure and difficult to understand) ... 
   * @returns array of coordinates
   */
  _getCoordArray(): number[] {
    let coordsArray = null;
    this.value = this.value.replace(/′/g, "'").replace(/″/g, '"');
    if (this.value.indexOf("'") > -1) {//pve ’’ ?  '' or "
      //lat lon EPSG:4326
      //belgium only N , E
      //xx° xx′ xx″ N, xx° xx′ xx″ E
      //xx° xx′ xx″ E, xx° xx′ xx″ N
      //xx° xx′ xx″ N, xx° xx′ xx″ E
      //xx° xx′ xx.xx″ N, xx° xx′ xx.xx″ E   ex. 51° 7' 24.442" N, 5° 7' 24.474" E
      //xx° xx′ xx.xx″, xx° xx′ xx.xx″
      this.value = this.value.toUpperCase();
      if ((this.value.indexOf("S") === -1) && (this.value.indexOf("W") === -1)
        && (this.value.indexOf("'") !== this.value.lastIndexOf("'")) && (this.value.indexOf("\"") > -1)
        && (this.value.indexOf("\"") !== this.value.lastIndexOf("\""))) {
        this.value = this.value.replace(/(°|'|")/g, ' ').replace(/[A-Z]/g, ' ').replace(/\s{2,}/g, ' ').replace(/^\s+|\s+$/g, '');
        var parts = this.value.split(/[^\d\w\.]+/);
        if (parts.length === 6) {
          //if necessary we will switch x and y afterwards, for now we presume lat lon, not lon lat
          var y = this._convertDMSToDD(parts[0], parts[1], parts[2]);
          var x = this._convertDMSToDD(parts[3], parts[4], parts[5]);
          this.fullDegreesLocationString = [parts[0] + '°' + parts[1] + '\'' + parts[2] + '\'\'', parts[3] + '°' + parts[4] + '\'' + parts[5] + '\'\''];
          coordsArray = [y, x];
        }
      }
    } else {
      //lat lon EPSG:4326
      //xx.xxx°, xx.xxx°
      //xx.xxx°, N xx.xxx° E
      this.value = this.value.toUpperCase().replace(/°/g, ' ').replace(/[A-Z]/g, ' ').replace(/\s{2,}/g, ' ').replace(/^\s+|\s+$/g, '');
      //var xy = val.split(' ');
      var xy = this.value.split(',');
      if (xy.length === 2) {
        //if necessary we will switch x and y afterwards, for now we presume lat lon, not lon lat
        var y = parseFloat(xy[0]);
        var x = parseFloat(xy[1]);
        coordsArray = [y, x];
      }
    }
    return coordsArray;
  }

  /**
   * Converts degrees, minutes, and seconds format to decimal degrees format
   * @param degrees 
   * @param minutes 
   * @param seconds 
   * @returns decimal degrees format as number
   */
  private _convertDMSToDD(degrees: string, minutes: string, seconds: string) {
    var dd: number = Number(degrees) + Number(minutes) / 60 + Number(seconds) / (3600);
    return dd;
  }

  /**
   * Returns a textual description of the coordinates
   * @param coordArray array of coordinates
   * @param coordSystem coordinate system as string 
   * @returns textual description of the coordinates as string
   */
  private _getCoordAsString(coordArray: number[], coordSystem: string): string {
    let locationString: string = null;
    if (coordSystem === "Lb08") {
      locationString = "x: " + coordArray[0].toFixed(2) + " m <br />y: " + coordArray[1].toFixed(2) + " m";
    } else if (coordSystem === "Lb72") {
      locationString = "x: " + coordArray[0].toFixed(2) + " m <br />y: " + coordArray[1].toFixed(2) + " m";
    } else if (coordSystem === "WGS84") {
      if (this.fullDegreesLocationString != null) {
        let phi: string = this._formatDMSCoord(this.fullDegreesLocationString[0])
        let lamda: string = this._formatDMSCoord(this.fullDegreesLocationString[1])
        locationString = "φ: " + phi + " N<br /> λ: " + lamda + " E";
      } else {
        locationString = "φ: " + coordArray[1].toFixed(6) + "°<br /> λ: " + coordArray[0].toFixed(6) + "°";
      }
    }
    return locationString;
  }

  private _formatDMSCoord(dmsCoord: string) {
    let dmsArray: string[] = dmsCoord.trim().split(/°|'|"/).slice(0,3);
    let d: number = Number(dmsArray[0]);
    let m: number = Number(dmsArray[1]);
    let s: number = Number(dmsArray[2]);

    if(s >= 60){
      let remainder: number = s % 60;
      let division: number = Math.floor(s / 60);
      s = remainder;
      m += division;
    } 

    if(m >= 60){
      let remainder: number = m % 60; 
      let division: number = Math.floor(m / 60);
      m = remainder;
      d += division;
    }

    return d + "° " + m + "' " + s + "\"";    
  }


  /**
   * Reprojects an array of coordinates
   * @param coordArray array of coordinates
   * @param coordSystem coordinate system as string 
   * @returns reprojected coordinates as number array
   */
  private _reprojectCoordinates(coordArray: number[], coordSystem: string) {
    let coords = null;
    if (coordSystem === "Lb08") {
      coords = olProj.transform(coordArray, 'EPSG:3812', this.mapProjectionCode);
    } else if (coordSystem === "Lb72") {
      coords = olProj.transform(coordArray, 'EPSG:31370', this.mapProjectionCode);
    } else if (coordSystem === "WGS84") {
      //reverse if needed
      if (Extent.containsCoordinate(this.WGS84Extent, coordArray) || Extent.containsCoordinate(this.WGS84Extent, coordArray.reverse())) {
        coords = olProj.transform(coordArray, 'EPSG:4326', this.mapProjectionCode);
      }
    }
    return coords;
  }

  /**
   * Returns the coordinate system as string
   * @param coordArray array of coordinates
   * @returns a coordinate system as string
   */
  private _getCoordSystem(coordArray: number[]): string {
    let coordSystem = null;
    if (Extent.containsCoordinate(this.Lb08Extent, coordArray)) {
      coordSystem = "Lb08";
    } else if (Extent.containsCoordinate(this.Lb72Extent, coordArray)) {
      coordSystem = "Lb72";
    } else if (Extent.containsCoordinate(this.WGS84Extent, coordArray) || Extent.containsCoordinate(this.WGS84Extent, coordArray.reverse())) {
      coordSystem = "WGS84";
    }
    return coordSystem;
  }
}

/**
 * This class is used only inside CoordinatesService, no need to create a separate file.
 */
class CoordinateExtractor {
  private static DMS_REG = new RegExp("^\\s*[0-9]+\\s*°\\s*[0-9]+\\s*'\\s*[0-9]+(.[0-9]+)?\\s*\"\\s*[NESO]?\\s*$");
  private static XY_REG = new RegExp("^\\s*[0-9]+(.[0-9]+)?\\s*$");
  private static UTM_REG: RegExp = new RegExp("^\\s*[0-9]{2}N|n\\s*[0-9]+(.[0-9]+)?\\s*$");
  private mapProjectionCode: string;

  constructor(mapProjectionCode: string) {
    this.mapProjectionCode = mapProjectionCode;
  }

  /* ===================================== */
  /* PUBLIC
  /* ===================================== */

  /**
   * Builds the TTCoordinate object
   * @param value string coordinates 
   * @returns TTCoordinate object
   */
  public getCoordinates(value: string): TTCoordinate {
    value = value.trim();
    let coordinates: Coord = this._getCoordinatesType(value);
    //console.dir(coordinates)
    return coordinates != null ? coordinates.getCoordinates(): null;
  }

  /* ===================================== */
  /* PRIVATE
  /* ===================================== */

  /**
   * Converts string coordinates into typed objects (XY, DMS & UTM)
   * @param value coordinates as string
   * @returns Coord object
   */
  private _getCoordinatesType(value: string): Coord {

    if (this._isDMSCoordinates(value)) {
      return new DSMCoord(value, this.mapProjectionCode);
    }

    if (this._isUTMCoordinates(value)) {
      return new UTMCoord(value, this.mapProjectionCode);
    }

    if (this._isXYCoordinates(value)) {
      return new XYCoord(value, this.mapProjectionCode);
    }

    return null;
  }

  /**
   * Verifies that the type of coordinates is DMS.
   * @param stringCoordinates coordinates as string
   * @returns true if the DMS regex matches.
   */
  private _isDMSCoordinates(stringCoordinates: string): boolean {
    return this._testRegex(stringCoordinates, CoordinateExtractor.DMS_REG);
  }

  /**
   * Verifies that the type of coordinates is XY.
   * @param stringCoordinates coordinates as string
   * @returns true if the XY regex matches.
   */
  private _isXYCoordinates(stringCoordinates: string): boolean {
    // must contain a separator (,)
    if (stringCoordinates.indexOf(',') === -1)
      return false;
    return this._testRegex(stringCoordinates, CoordinateExtractor.XY_REG);
  }

  /**
   * Verifies that the type of coordinates is UTM.
   * @param stringCoordinates coordinates as string
   * @returns true if the UTM regex matches.
   */
  private _isUTMCoordinates(stringCoordinates: string): boolean {
    return this._testRegex(stringCoordinates, CoordinateExtractor.UTM_REG, true);
  }

  /**
   * Used to test a given regex.
   * @param stringCoordinates coordinates as string
   * @param regex RegExp object
   * @param noSplit if true the regex will be applied on the whole string
   *  otherwise the regex will split the string in two parts and test the regexp on both parts.
   * @returns returns true if the regex matches.
   */
  private _testRegex(stringCoordinates: string, regex: RegExp, noSplit: boolean = false): boolean {

    if (stringCoordinates.indexOf(',') === -1 || noSplit)
      return regex.test(stringCoordinates);

    let splittedCoordStringArray: string[] = this._splitCoordinateStringToArray(stringCoordinates);
    
    if(splittedCoordStringArray.length != 2){
      return false; 
    }

    let valid: boolean = true;
    splittedCoordStringArray.forEach((coord: string) => {
      if (!regex.test(coord))
        valid = false;
    });
    return valid;
  }

  /**
   * Splits coordinates to array.
   * @param stringCoordinates coordinates as string
   * @returns array of string coordinates 
   */
  private _splitCoordinateStringToArray(stringCoordinates: string): string[] {
    let coordStringArray: string[] = stringCoordinates.split(",");
    return coordStringArray;
  }

}
